"use client";

import { useState } from "react";
import {
  Badge,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";

export default function CampaignApplicationTableItem({
  application,
  index,
  onViewDetails,
  onStatusUpdate,
  onDelete,
}) {
  const [isUpdating, setIsUpdating] = useState(false);

  // Helper functions to safely render values
  const safeString = (value) => {
    if (value === null || value === undefined) return "N/A";
    if (typeof value === "object") return "N/A";
    return String(value);
  };

  const safeNumber = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === "object") return 0;
    return Number(value) || 0;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "warning", text: "Pending" },
      approved: { color: "success", text: "Approved" },
      rejected: { color: "danger", text: "Rejected" },
      completed: { color: "primary", text: "Completed" },
    };

    const config = statusConfig[status] || {
      color: "secondary",
      text: "Unknown",
    };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(safeNumber(price));
  };

  const handleStatusUpdate = async (newStatus) => {
    setIsUpdating(true);
    try {
      await onStatusUpdate(application.id, newStatus);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = () => {
    if (
      window.confirm(
        "Are you sure you want to delete this application? This action cannot be undone."
      )
    ) {
      onDelete(application.id);
    }
  };

  return (
    <tr>
      <td>{index + 1}</td>
      <td>
        <div className="d-flex align-items-center">
          {application.influencer?.profile_image && (
            <img
              src={
                "https://api.tafaseelapp.com/storage/" +
                  application.influencer.profile_image || "/placeholder.svg"
              }
              alt={safeString(application.influencer?.name)}
              className="rounded-circle me-2"
              style={{ width: "32px", height: "32px", objectFit: "cover" }}
            />
          )}
          <div>
            <div className="fw-medium">
              {safeString(application.influencer?.name)}
            </div>
            <small className="text-muted">
              {safeString(application.influencer?.email)}
            </small>
          </div>
        </div>
      </td>
      <td>
        <div>
          <div className="fw-medium">
            {safeString(application.campaign?.title)}
          </div>
          <small className="text-muted">
            {formatPrice(application.campaign?.price)}
          </small>
        </div>
      </td>
      <td>
        <div>
          <div className="fw-medium">
            {safeString(application.campaign?.user?.name)}
          </div>
          <small className="text-muted">
            {safeString(application.campaign?.user?.email)}
          </small>
        </div>
      </td>
      <td>{getStatusBadge(application.status)}</td>
      <td>
        <div>
          <div>
            {formatDate(application.applied_at || application.created_at)}
          </div>
          {application.reviewed_at && (
            <small className="text-muted">
              Reviewed: {formatDate(application.reviewed_at)}
            </small>
          )}
        </div>
      </td>
      <td>
        <div>
          <div>
            {safeNumber(
              application.influencer?.followers_count
            ).toLocaleString()}
          </div>
          <small className="text-muted">
            {safeNumber(application.influencer?.engagement_rate)}% engagement
          </small>
        </div>
      </td>
      <td>
        {application.message ? (
          <div
            className="text-truncate"
            style={{ maxWidth: "150px" }}
            title={application.message}
          >
            {safeString(application.message)}
          </div>
        ) : (
          <span className="text-muted">No message</span>
        )}
      </td>
      <td>
        <div className="d-flex gap-1">
          <Button
            color="info"
            size="sm"
            onClick={() => onViewDetails(application)}
            title="View Details"
          >
            <i className="ri-eye-line"></i>
          </Button>

          <UncontrolledDropdown>
            <DropdownToggle
              color="secondary"
              size="sm"
              caret
              disabled={isUpdating}
            >
              <i className="ri-more-2-line"></i>
            </DropdownToggle>
            <DropdownMenu>
              {application.status === "pending" && (
                <>
                  <DropdownItem onClick={() => handleStatusUpdate("approved")}>
                    <i className="ri-check-line me-2 text-success"></i>
                    Approve
                  </DropdownItem>
                  <DropdownItem onClick={() => handleStatusUpdate("rejected")}>
                    <i className="ri-close-line me-2 text-danger"></i>
                    Reject
                  </DropdownItem>
                  <DropdownItem divider />
                </>
              )}

              {application.status === "approved" && (
                <>
                  <DropdownItem onClick={() => handleStatusUpdate("completed")}>
                    <i className="ri-check-double-line me-2 text-primary"></i>
                    Mark Complete
                  </DropdownItem>
                  <DropdownItem onClick={() => handleStatusUpdate("rejected")}>
                    <i className="ri-close-line me-2 text-danger"></i>
                    Reject
                  </DropdownItem>
                  <DropdownItem divider />
                </>
              )}

              {application.status !== "pending" && (
                <>
                  <DropdownItem onClick={() => handleStatusUpdate("pending")}>
                    <i className="ri-time-line me-2 text-warning"></i>
                    Reset to Pending
                  </DropdownItem>
                  <DropdownItem divider />
                </>
              )}

              <DropdownItem onClick={handleDelete} className="text-danger">
                <i className="ri-delete-bin-line me-2"></i>
                Delete
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
      </td>
    </tr>
  );
}
